import { PropType, reactive } from 'vue';
import BaseSetupService from '@/setup/BaseSetupService';
import OrganizationUserEntity from '@/entity/organization-user/OrganizationUserEntity';
import CompanyApiClient from '@/api/CompanyApiClient';
import CompanyApiAgency from '@/api/CompanyApiAgency';
import { Bus, itemIs, useNotif } from '@orion.ui/orion';
import UserClientEntity from '@/entity/user-client/UserClientEntity';
import { SUITE_URL } from '@/utils/CONST';
import { PERMISSION_ACTION } from '@/typings/enums/PERMISSION_ACTION';
import CompanyApi from '@/api/CompanyApi';

type Props = SetupProps<typeof OrganizationUserCompaniesSetupService.props>
type Company = ICompanyForDetailOrganizationAgencyUserModel | ICompanyForDetailOrganizationClientUserModel

export default class OrganizationUserCompaniesSetupService extends BaseSetupService<Props> {
	static readonly props = {
		readonly: Boolean,
		userIsAgencyWatchingClient: Boolean,
		user: {
			type: Object as PropType<OrganizationUserEntity | UserClientEntity>,
			required: true as const,
		},
	};

	private _aside?: OrionAside;

	private readonly state = reactive({
		companiesWithMemberships: [] as ICompanyWithMembershipModel[],
		companies: [] as Company[],
		count: 0,
		page: {
			size: 20,
			index: 1,
		},
		filter: {
			order: {
				property: 'Name',
				ascending: true,
			},
		},
	});

	get count () { return this.state.count; }
	get filter () { return this.state.filter; }
	get companiesWithMemberships () { return this.state.companiesWithMemberships; }

	get orderConfig () {
		const config = {
			current: this.filter.order,
			orders: [
				{
					key: 'Name',
					label: this.self.isAgency() ? 'Nom' : 'Société',
				},
			],
		};

		if (this.self.isClient()) {
			config.orders.push({
				key: 'AgencyCompany.Name',
				label: `Agence`,
			});
		}
		return config;
	}

	get companies () { return this.state.companies as Company[]; }
	set companies (val) { this.state.companies = val; }

	get page () { return this.state.page; }
	set page (val) { this.state.page = val; }


	constructor (props?: Props, _aside?: OrionAside) {
		super(props);
		this._aside = _aside;
	}

	protected async onBeforeMountAsync () {
		await this.refreshListAsync();
	}


	async refreshListAsync (resetPagination = false) {
		if (!this.organizationGuard() || !this.props.user.userId) return;

		const api = this.self.isClient()
			? CompanyApiClient
			: CompanyApiAgency;

		try {
			this._aside?._loader()?.show('Chargement des sociétés...');

			if (resetPagination) this.state.page.index = 1;

			if (this.props.userIsAgencyWatchingClient && itemIs<typeof CompanyApiAgency>(api, 'getClientCompaniesByUserId')) {
				const { data } = await api.getClientCompaniesByUserId(this.props.user.userId, {
					...this.filter,
					organizationId: this.self.organization.id,
					page: this.page,
				});
				this.state.companies = data.list;
				this.state.count = data.count;
			} else {
				const { data } = await api.getCompaniesByUserId(this.props.user.userId, {
					...this.filter,
					organizationId: this.self.organization.id,
					page: this.page,
				});
				this.state.companies = data.list;
				this.state.count = data.count;
			}

			// Load companies with memberships
			await this.getCompaniesWithMembershipsAsync();

			Bus.emit(
				`OrganizationUserStickerSetupService:${this.props.user.userId}:refreshUserCompanies`,
				this.state.companiesWithMemberships.filter(x => !!x.membershipId),
			);

		} catch (e: any) {
			useNotif.danger(e?.response?.data);
		} finally {
			this._aside?._loader()?.hide();
		}
	}

	async deleteMembershipAsync (company: Company) {
		if (company.membershipId && this.isOrganizationUser()) {
			await this.props.user.deleteMembershipAsync(company.membershipId);
			this.state.companies.deleteWhere('membershipId', company.membershipId);
			const targetCompanyWithMembership = this.state.companiesWithMemberships.findByKey(company.id, 'id');
			if (targetCompanyWithMembership) {
				targetCompanyWithMembership.membershipId = undefined;
				Bus.emit(
					`OrganizationUserStickerSetupService:${this.props.user.userId}:refreshUserCompanies`,
					this.state.companiesWithMemberships.filter(x => !!x.membershipId),
				);
			}
			this.state.count--;
		}
	}

	goToSuiteMemberships () {
		if (this.props.user instanceof OrganizationUserEntity) {
		  window.location.assign(SUITE_URL + `/utilisateurs/${this.props.user.suiteUserId}/agences?source=Armado&sourceUrl=${location.href}`);
		}
	}

	isOrganizationUser (): this is {props: {user: OrganizationUserEntity }} {
		return this.props.user instanceof OrganizationUserEntity;
	}

	async getCompaniesWithMembershipsAsync () {
		if (!this.organizationGuard() || !this.props.user.userId) return;
		const { data } = await CompanyApi.getCompaniesWithMembershipsByUserId({
			organizationId: this.self.organization.id,
			targetUserId: this.props.user.userId,
			permissionAction: PERMISSION_ACTION.UserRead,
		});
		this.state.companiesWithMemberships = data;
	}
}
